import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import type { AuthUser } from './oidc';
import { getOidc, useOidc } from './oidc';
import { useLoginWithRedirectInternal } from './use-login-with-redirect-internal/use-login-with-redirect-internal';
import { useLogoutInternal } from './use-logout-internal/use-logout-internal';

export type { AuthUser } from './oidc';

export interface LoginRedirectOptions {
  /** Where to return after login (oidc-spa `redirectUrl`). */
  redirectUrl?: string;
  /** OIDC `login_hint` — see docs/authentication/02-login-and-idp-hints.md. */
  loginHint?: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  loginWithRedirect: (options?: LoginRedirectOptions) => Promise<void>;
  logout: (errorMessage?: string) => Promise<void>;
  user: AuthUser | undefined;
  getAccessTokenSilently: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const oidc = useOidc();

  // Re-thrown below (after all hooks) so the boundary shows ErrorBoundaryPage rather
  // than oidc-spa's native alert()/hang. See docs/authentication/01 (Error handling).
  const initializationError = oidc.isUserLoggedIn
    ? undefined
    : oidc.initializationError;

  const isAuthenticated = oidc.isUserLoggedIn;
  const user: AuthUser | undefined = oidc.isUserLoggedIn
    ? oidc.decodedIdToken
    : undefined;

  const logout = useLogoutInternal();
  const { loginWithRedirect: loginWithRedirectInternal } =
    useLoginWithRedirectInternal();
  const isRedirectingRef = useRef(false);

  // Imperative token accessor for parity with the old Auth0 facade (e.g. Ask Ben).
  const getAccessTokenSilently = useCallback(async () => {
    const oidcInstance = await getOidc();
    if (!oidcInstance.isUserLoggedIn) {
      throw new Error('Cannot get access token: no active session');
    }
    return oidcInstance.getAccessToken();
  }, []);

  const loginWithRedirect = useCallback(
    async (options?: LoginRedirectOptions) => {
      if (isRedirectingRef.current) {
        return;
      }
      isRedirectingRef.current = true;

      try {
        return await loginWithRedirectInternal(options);
      } catch (error) {
        isRedirectingRef.current = false;
        throw error;
      }
    },
    [loginWithRedirectInternal]
  );

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      loginWithRedirect,
      logout,
      user,
      getAccessTokenSilently,
    }),
    [isAuthenticated, loginWithRedirect, logout, user, getAccessTokenSilently]
  );

  if (initializationError) {
    throw initializationError;
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
